import { useState } from 'react';
import { prefs, t } from './i18n.js';
import { navigate } from './router.jsx';
import { Icon, LangButton, Logo } from './ui.jsx';

// First visit: ask the visitor's goal, then explain what the verification labels mean.
// No browser permission is requested here: the site has in-app and email notifications only, so there
// is nothing to ask for until push notifications exist.
export function Welcome() {
  const [step, setStep] = useState(1);
  const [role, setRole] = useState('');
  // 'browse' keeps the chosen role for later and goes to the listings; 'register' opens the sign-up form.
  const finish = (chosen, to) => {
    prefs.finish(chosen);
    navigate(to === 'register' && chosen ? `/register?role=${chosen}` : '/', { replace: true });
  };
  const buyer = role === 'buyer';
  const roles = [
    ['buyer', Icon.search, t('กำลังหาทรัพย์', 'Find a property'), t('ค้นหา บันทึก และติดต่อผู้ลงประกาศได้โดยตรง', 'Search, save and contact listers directly')],
    ['owner', Icon.house, t('เจ้าของทรัพย์', 'Property owner'), t('ลงประกาศขายหรือให้เช่าได้ง่ายใน 3 ขั้นตอน', 'List a property for sale or rent in 3 simple steps')],
    ['agent', Icon.building, t('นายหน้า', 'Agent'), t('จัดการประกาศ ผู้สนใจ และการติดต่อไว้ในที่เดียว', 'Manage listings, enquiries and conversations in one place')],
  ];
  return (
    <div className="welcome">
      <div className="card stack welcome-card">
        <LangButton className="langbtn corner" />
        <Logo className="welcome-logo" />
        <div className="steps" aria-hidden="true">{[1, 2].map((i) => <span key={i} className={i <= step ? 'on' : undefined} />)}</div>
        {step === 1 && (
          <>
            <div className="stack" style={{ gap: 7 }}>
              <span className="cap">{t('เริ่มต้นง่าย ๆ', 'A simpler way to move')}</span>
              <h1>{t('วันนี้คุณต้องการทำอะไร', 'What would you like to do today?')}</h1>
              <p className="muted">{t('เลือกเป้าหมายเพื่อให้เราแสดงสิ่งที่เกี่ยวข้องกับคุณมากที่สุด คุณเปลี่ยนได้ภายหลัง', 'Choose your goal so we can make OneProp more relevant. You can change it later.')}</p>
            </div>
            <div className="choices" role="radiogroup" aria-label={t('เป้าหมายการใช้งาน', 'Your goal')}>
              {roles.map(([value, icon, title, text]) => (
                <button type="button" key={value} role="radio" aria-checked={role === value} className={`choice${role === value ? ' on' : ''}`} onClick={() => setRole(value)}>
                  <span className="choice-ico">{icon}</span><span><strong>{title}</strong><small>{text}</small></span>{Icon.right}
                </button>
              ))}
            </div>
            <div className="stack">
              <button type="button" className="btn primary block" disabled={!role} onClick={() => setStep(2)}>{t('ดำเนินการต่อ', 'Continue')}{Icon.right}</button>
              <button type="button" className="btn ghost block" onClick={() => finish('', 'browse')}>{t('เข้าชมก่อน ยังไม่ต้องสมัคร', 'Browse first, no account needed')}</button>
              <button type="button" className="linkbtn" onClick={() => { prefs.finish(''); navigate('/login', { replace: true }); }}>{t('มีบัญชีแล้ว เข้าสู่ระบบ', 'Already have an account? Sign in')}</button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <div className="stack" style={{ gap: 7 }}>
              <span className="cap">{Icon.verified} {t('ออกแบบมาเพื่อความมั่นใจ', 'Built for confidence')}</span>
              <h1>{t('หาทรัพย์และติดต่อกันได้อย่างมั่นใจขึ้น', 'Find and contact with more confidence')}</h1>
              <p className="muted">{t('OneProp แยกสถานะการยืนยันให้เห็นชัด ช่วยให้ข้อมูลประกาศเป็นระเบียบ และให้คุณติดต่อผู้ลงประกาศได้ตรงจากหน้าเดียว', 'OneProp makes verification status clear, keeps listing details structured, and lets you contact the lister directly from one place.')}</p>
            </div>
            <div className="welcome-trust">
              <div><span className="trust-ico">{Icon.verified}</span><span><b>{t('เห็นสถานะการตรวจสอบชัดเจน', 'Clear verification status')}</b><small>{t('แยกผู้ยืนยันตัวตนและประกาศที่ตรวจเอกสารสิทธิ์แล้ว', 'See identity and ownership checks when available')}</small></span></div>
              <div><span className="trust-ico">{Icon.chat}</span><span><b>{t('ติดต่อโดยตรง', 'Direct contact')}</b><small>{t('ข้อความ แชต โทรศัพท์ หรือ LINE ตามช่องทางที่ผู้ลงประกาศเปิดไว้', 'Message, chat, phone or LINE when the lister enables it')}</small></span></div>
              <div><span className="trust-ico">{Icon.tune}</span><span><b>{t('ข้อมูลเป็นระบบ ค้นหาง่าย', 'Structured and easy to search')}</b><small>{t('ราคา ทำเล รายละเอียด และตัวกรองสำคัญอยู่ในรูปแบบเดียวกัน', 'Price, location, details and key filters stay consistent')}</small></span></div>
            </div>
            <div className="stack">
              <button type="button" className="btn primary block" onClick={() => finish(role, buyer ? 'browse' : 'register')}>{buyer ? t('เริ่มค้นหาทรัพย์', 'Start searching') : t('สร้างบัญชีและเริ่มใช้งาน', 'Create account and get started')}{Icon.right}</button>
              <button type="button" className="btn ghost block" onClick={() => finish(role, buyer ? 'register' : 'browse')}>{buyer ? t('สมัครสมาชิกฟรี', 'Create a free account') : t('เข้าชมก่อน', 'Browse first')}</button>
              <button type="button" className="linkbtn" onClick={() => setStep(1)}>{t('ย้อนกลับ', 'Back')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
